import {
  type SelectionEffectFactory,
  type SelectionInputFactory,
} from '@databases/selections';

/**
 * Well-known feature keys for the core selection features.
 * Using 'const' object + value extraction instead of enum
 * so the type remains open for extension.
 */
export const SelectionFeatureKeys = {
  // ── Inputs ──────────────────────────────
  CONTEXT_MENU_INPUT: 'context-menu-input',
  KEYBOARD_INPUT: 'keyboard-input',
  MOUSE_INPUT: 'mouse-input',
  VIEW_CHANGES_INPUT: 'view-changes-input',
  YJS_CHANGES_INPUT: 'yjs-changes-input',

  // ── Effects ─────────────────────────────
  ACTION_BAR_EFFECT: 'action-bar-effect',
  CELL_EDITING_EFFECT: 'cell-editing-effect',
  CHECKBOX_EFFECT: 'checkbox-effect',
  CLEAR_CELLS_EFFECT: 'clear-cells-effect',
  CONTEXT_MENU_EFFECT: 'context-menu-effect',
  DELETE_INTENT_HIGHLIGHT_EFFECT: 'delete-intent-highlight-effect',
  FILL_EFFECT: 'fill-effect',
  FILL_PREVIEW_EFFECT: 'fill-preview-effect',
  FOCUS_BOX_EFFECT: 'focus-box-effect',
  HIGHLIGHTS_EFFECT: 'highlights-effect',
  PRESENCE_EFFECT: 'presence-effect',
  PUBLISH_ROVO_EFFECT: 'publish-rovo-effect',
  SELECTIONS_PING_EFFECT: 'selections-ping-effect',
} as const;

/**
 * Open string literal union.
 *
 * Core keys get full IDE autocomplete. The `(string & {})` escape hatch
 * allows downstream code, A/B tests, or runtime plugins to inject custom
 * feature keys without modifying this file.
 *
 *  type SelectionFeatureKey = 'view-changes' | 'cell-editing' | ... | (string & {})
 */
export type SelectionFeatureKey =
  | (typeof SelectionFeatureKeys)[keyof typeof SelectionFeatureKeys]
  | (string & NonNullable<unknown>);

/**
 * Structural subset of DatabasePermissions from @databases/permissions.
 *
 * Duplicated here to avoid adding @databases/permissions as a dependency
 * of @databases/selections. The structural typing ensures compatibility
 * with the real DatabasePermissions at call sites.
 */
export interface SelectionPermissions {
  readonly canEditContent: boolean;
  readonly canEditFields: boolean;
  readonly canDelete: boolean;
  readonly isLocked: boolean;
  readonly isViewingVersion: boolean;
}

/**
 * A selection feature is either an input or an effect that can be
 * conditionally included by a mode and self-filtered by permissions.
 */
export type SelectionFeature = SelectionInputFeature | SelectionEffectFeature;

interface SelectionFeatureBase {
  /** Stable identifier — used for debugging, logging, and deduplication. */
  key: SelectionFeatureKey;

  /**
   * Optional permission gate. If provided, the feature is EXCLUDED
   * when this returns false. If omitted, the feature is always active
   * (assuming the mode included it).
   *
   * Receives a SelectionPermissions object that is structurally
   * compatible with DatabasePermissions from @databases/permissions.
   */
  isEnabled?: (perms: SelectionPermissions) => boolean;
}

export interface SelectionInputFeature extends SelectionFeatureBase {
  type: 'input';
  /**
   * Pre-bound factory — dependencies already captured via closure.
   * Called with the machine to produce the SelectionInput lifecycle.
   */
  factory: SelectionInputFactory;
}

export interface SelectionEffectFeature extends SelectionFeatureBase {
  type: 'effect';
  /**
   * Pre-bound factory — dependencies already captured via closure.
   * Returns a SelectionEffect when called.
   */
  factory: SelectionEffectFactory;
}
